
import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Users, ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/utils/supabaseHelpers";

interface UpcomingStream {
  id: string;
  title: string;
  start_date: string;
  description?: string;
  stream_url?: string;
  image_url?: string;
}

export default function UpcomingStreamPreview() {
  const [upcomingStream, setUpcomingStream] = useState<UpcomingStream | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUpcomingStream() {
      try {
        const now = new Date().toISOString();
        
        // Get the next upcoming auction with stream URL using direct query
        const { data, error } = await supabase
          .from("auctions")
          .select("*")
          .gt("start_date", now)
          .not("stream_url", "is", null)
          .order("start_date", { ascending: true })
          .limit(1);

        if (error) throw error;

        // Get the closest upcoming auction
        const nextStream = data && data.length > 0 ? data[0] : null;
        if (nextStream && 'start_date' in nextStream) {
          const upcomingStreamData = nextStream as unknown as UpcomingStream;
          setUpcomingStream(upcomingStreamData);
          calculateTimeRemaining(new Date(upcomingStreamData.start_date));
        }
      } catch (error) {
        console.error("Error fetching upcoming stream:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchUpcomingStream();

    // Set up an interval to update the countdown
    const interval = setInterval(() => {
      if (upcomingStream?.start_date) {
        calculateTimeRemaining(new Date(upcomingStream.start_date));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [upcomingStream?.id]);

  function calculateTimeRemaining(startDate: Date) {
    const now = new Date();
    const difference = startDate.getTime() - now.getTime();

    if (difference <= 0) {
      setTimeRemaining("Starting soon");
      return;
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
    } else if (hours > 0) {
      setTimeRemaining(`${hours}h ${minutes}m`);
    } else {
      setTimeRemaining(`${minutes}m`);
    }
  }

  if (loading) {
    return (
      <Card className="h-full bg-white/5 backdrop-blur-xl border border-white/10 shadow-xl">
        <CardHeader className="animate-pulse bg-white/5 h-12 rounded-lg" />
        <CardContent className="animate-pulse bg-white/5 h-48 rounded-lg" />
      </Card>
    );
  }

  if (!upcomingStream) {
    return (
      <Card className="h-full flex flex-col justify-center items-center p-6 text-center bg-white/5 backdrop-blur-xl border border-white/10 shadow-xl">
        <Calendar className="h-12 w-12 text-blue-400 mb-4" />
        <h3 className="text-lg font-light mb-2 text-white tracking-wide">No Upcoming Streams</h3>
        <p className="text-white/70 mb-4 font-light">
          Check back soon for our next live auction stream.
        </p>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col overflow-hidden bg-white/5 backdrop-blur-xl border border-white/10 shadow-xl hover:shadow-2xl transition-all duration-300 group">
      {/* Subtle glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-blue-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <CardHeader className="pb-2 relative">
        <div className="flex justify-between items-start gap-2">
          <Badge className="bg-red-500/20 text-red-300 border border-red-400/30 backdrop-blur-xl font-light tracking-wide">
            Upcoming Stream
          </Badge>
          <div className="flex items-center gap-1 bg-blue-500/20 text-blue-300 border border-blue-400/30 backdrop-blur-xl py-1 px-3 rounded-full text-xs font-light">
            <Clock className="h-3 w-3" />
            <span>{timeRemaining}</span>
          </div>
        </div>
        <CardTitle className="text-xl mt-2 line-clamp-1 text-white font-light tracking-wide">{upcomingStream.title}</CardTitle>
      </CardHeader>

      <CardContent className="pb-4 flex-grow relative">
        <AspectRatio ratio={16 / 9} className="mb-4 bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10">
          <div className="relative w-full h-full">
            {upcomingStream.image_url ? (
              <img 
                src={upcomingStream.image_url}
                alt={upcomingStream.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500/20 to-blue-500/20">
                <Calendar className="h-12 w-12 text-blue-400" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
              <div className="flex items-center text-white text-sm font-light">
                <Calendar className="h-4 w-4 mr-2 text-emerald-400" />
                <span>
                  {new Date(upcomingStream.start_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          </div>
        </AspectRatio>

        <p className="text-white/70 line-clamp-2 mb-3 font-light">
          {upcomingStream.description || "Join us for this exclusive live auction featuring authentic Florida Highwaymen paintings."}
        </p>

        <div className="flex items-center text-sm text-white/60 font-light">
          <Users className="h-4 w-4 mr-1 text-emerald-400" />
          <span>Registration open</span>
        </div>
      </CardContent>

      <CardFooter className="pt-0 relative">
        <Link to={`/auctions/${upcomingStream.id}`} className="w-full">
          <Button className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-light shadow-lg hover:shadow-xl transition-all duration-300 border-0 backdrop-blur-xl tracking-wide hover:scale-[1.02] hover:-translate-y-0.5">
            Register Now <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}
